/**
 * billToParty — addressing a Quick Bill to a supplier.
 *
 * An invoice can only point at a CUSTOMER (`invoices.customer_id`); there is no
 * supplier column, and adding one is a schema change. A merchant billing a
 * supplier therefore bills that supplier *as a customer*: the first time, a
 * customer record is made from the supplier's name, phone, GSTIN and address;
 * afterwards the same record is found again rather than duplicated.
 *
 * Matching, strongest first — GSTIN, then phone, then exact name. A GSTIN or a
 * phone that disagrees is a different party even if the names match (two
 * "Sharma Traders" in different states are two businesses).
 */
import { Q } from '@nozbe/watermelondb';
import { txn } from '@/db';
import { Customer } from '@/db/models/Customers';
import type { Supplier } from '@/db/models/Inventory';

export interface PartyLike {
  name: string | null;
  phone: string | null;
  gstin: string | null;
}

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();
const digits = (s: string | null | undefined) => (s ?? '').replace(/\D/g, '').slice(-10);

/** The existing customer that IS this supplier, or null. Pure. */
export function matchCustomerForParty<C extends PartyLike>(party: PartyLike, customers: readonly C[]): C | null {
  const gstin = norm(party.gstin);
  if (gstin) {
    // A GSTIN identifies a business outright; a customer with a different one
    // is someone else, whatever the name.
    return customers.find((c) => norm(c.gstin) === gstin) ?? null;
  }
  const phone = digits(party.phone);
  if (phone.length === 10) {
    const byPhone = customers.find((c) => digits(c.phone) === phone);
    if (byPhone) return byPhone;
  }
  const name = norm(party.name);
  if (!name) return null;
  return (
    customers.find((c) =>
      norm(c.name) === name
      // Same name but a conflicting identity is a different party.
      && !norm(c.gstin)
      && !(phone.length === 10 && digits(c.phone).length === 10 && digits(c.phone) !== phone),
    ) ?? null
  );
}

/**
 * The customer to bill for this supplier in this store — found, or created
 * from the supplier's details.
 */
export async function customerForSupplier(storeId: string, supplier: Supplier): Promise<Customer> {
  return txn(async (db) => {
    const existing = await db
      .get<Customer>('customers')
      .query(Q.where('store_id', storeId), Q.where('is_deleted', false))
      .fetch();
    const hit = matchCustomerForParty(supplier, existing);
    if (hit) return hit;

    return db.get<Customer>('customers').create((record) => {
      record.storeId = storeId;
      record.name = supplier.name.trim();
      record.phone = digits(supplier.phone).length === 10 ? digits(supplier.phone) : null;
      record.email = supplier.email ?? null;
      record.gstin = supplier.gstin ? supplier.gstin.trim().toUpperCase() : null;
      record.pan = null;
      record.address = supplier.address ?? null;
      record.city = null;
      // Left null on purpose: place of supply is derived from the GSTIN's
      // state code (CartPanel), which is authoritative for a registered party.
      record.state = null;
      record.pincode = null;
      record.groupId = null;
      record.creditLimit = 0;
      record.balance = 0;
      record.loyaltyTierId = null;
      record.loyaltyPoints = 0;
      record.totalSpend = 0;
      record.visitCount = 0;
      record.lastVisitAt = null;
      record.birthday = null;
      record.anniversary = null;
      record.tags = null;
      record.version = 1;
      record.isSynced = false;
      record.isDeleted = false;
      record.lastSyncBalance = null;
      record.lastSyncTotalSpend = null;
      record.lastSyncLoyaltyPoints = null;
      record.lastSyncVisitCount = null;
    });
  });
}

// ─── Buyer typed on the invoice-style Quick Bill form ───────────────────────

export interface TypedBuyer {
  /** Set when the operator picked an existing customer from the suggestions. */
  customerId?: string | null;
  /** Set when they picked a supplier; billed through its customer record. */
  supplierId?: string | null;
  name: string;
  address: string;
  gstin: string;
  phone: string;
}

export interface BuyerPatch { address?: string; gstin?: string; phone?: string }

/**
 * What the typed details add to an existing record: only fields the record
 * does not have yet. Never overwrites — a typo on one bill must not rewrite a
 * customer's saved GSTIN. Pure.
 */
export function fillBlanks(
  existing: { address: string | null; gstin: string | null; phone: string | null },
  typed: Pick<TypedBuyer, 'address' | 'gstin' | 'phone'>,
): BuyerPatch {
  const patch: BuyerPatch = {};
  const address = typed.address.trim();
  const gstin = typed.gstin.trim().toUpperCase();
  const phone = digits(typed.phone);
  if (address && !(existing.address ?? '').trim()) patch.address = address;
  if (gstin && !(existing.gstin ?? '').trim()) patch.gstin = gstin;
  if (phone.length === 10 && digits(existing.phone).length !== 10) patch.phone = phone;
  return patch;
}

/** Problems with the typed buyer, in words. Empty = fine. Pure. */
export function buyerProblems(typed: Pick<TypedBuyer, 'name' | 'gstin' | 'phone'>, gstinPattern: RegExp): string[] {
  const out: string[] = [];
  const gstin = typed.gstin.trim().toUpperCase();
  const phone = typed.phone.trim();
  if (gstin && !gstinPattern.test(gstin)) out.push('The buyer’s GSTIN is not a valid 15-character number.');
  if (phone && digits(phone).length !== 10) out.push('The buyer’s phone must be 10 digits.');
  return out;
}

/**
 * The customer this bill goes to — picked, matched, or created — with any
 * details typed for the first time saved onto it.
 */
export async function resolveBuyer(storeId: string, typed: TypedBuyer): Promise<Customer> {
  let customer: Customer | null = null;

  if (typed.customerId) {
    customer = await txn((db) => db.get<Customer>('customers').find(typed.customerId!));
  } else if (typed.supplierId) {
    const supplier = await txn((db) => db.get<Supplier>('suppliers').find(typed.supplierId!));
    customer = await customerForSupplier(storeId, supplier);
  }

  if (!customer) {
    return txn(async (db) => {
      const existing = await db
        .get<Customer>('customers')
        .query(Q.where('store_id', storeId), Q.where('is_deleted', false))
        .fetch();
      const hit = matchCustomerForParty(typed, existing);
      if (hit) return applyPatch(hit, fillBlanks(hit, typed));
      return db.get<Customer>('customers').create((record) => {
        record.storeId = storeId;
        record.name = typed.name.trim();
        record.phone = digits(typed.phone).length === 10 ? digits(typed.phone) : null;
        record.email = null;
        record.gstin = typed.gstin.trim().toUpperCase() || null;
        record.pan = null;
        record.address = typed.address.trim() || null;
        record.city = null;
        record.state = null; // place of supply comes from the GSTIN's state code
        record.pincode = null;
        record.groupId = null;
        record.creditLimit = 0;
        record.balance = 0;
        record.loyaltyTierId = null;
        record.loyaltyPoints = 0;
        record.totalSpend = 0;
        record.visitCount = 0;
        record.lastVisitAt = null;
        record.birthday = null;
        record.anniversary = null;
        record.tags = null;
        record.version = 1;
        record.isSynced = false;
        record.isDeleted = false;
        record.lastSyncBalance = null;
        record.lastSyncTotalSpend = null;
        record.lastSyncLoyaltyPoints = null;
        record.lastSyncVisitCount = null;
      });
    });
  }

  const patch = fillBlanks(customer, typed);
  const found = customer;
  return Object.keys(patch).length ? txn(() => applyPatch(found, patch)) : found;
}

/** Must run inside a writer. */
async function applyPatch(c: Customer, patch: BuyerPatch): Promise<Customer> {
  if (Object.keys(patch).length === 0) return c;
  return c.update((r) => {
    if (patch.address !== undefined) r.address = patch.address;
    if (patch.gstin !== undefined) r.gstin = patch.gstin;
    if (patch.phone !== undefined) r.phone = patch.phone;
    r.version = c.version + 1;
    r.isSynced = false;
  });
}

/**
 * A typed buyer that is neither picked nor cleanly matched, but shares its
 * name with a saved customer — or null. Pure.
 *
 * Creating a second record silently would duplicate the customer; reusing the
 * saved one silently could print someone else's GSTIN on a tax invoice. So the
 * operator decides: pick the saved customer, or give this buyer a GSTIN/phone
 * that makes them a different party.
 */
export function nameClash<C extends PartyLike>(typed: TypedBuyer, customers: readonly C[]): C | null {
  if (typed.customerId || typed.supplierId) return null;
  const name = norm(typed.name);
  if (!name) return null;
  if (matchCustomerForParty(typed, customers)) return null; // a clean match: fine
  if (norm(typed.gstin) || digits(typed.phone).length === 10) return null; // identity given: a different party
  return customers.find((c) => norm(c.name) === name) ?? null;
}

/** The operator-facing problem for {@link nameClash}, read from the store. */
export async function buyerNameProblem(storeId: string, typed: TypedBuyer): Promise<string | null> {
  if (typed.customerId || typed.supplierId || !typed.name.trim()) return null;
  const sameName = await txn((db) => db
    .get<Customer>('customers')
    .query(Q.where('store_id', storeId), Q.where('is_deleted', false), Q.where('name', Q.like(Q.sanitizeLikeString(typed.name.trim()))))
    .fetch());
  const clash = nameClash(typed, sameName);
  if (!clash) return null;
  const id = [clash.gstin && `GSTIN ${clash.gstin}`, clash.phone && `ph ${clash.phone}`].filter(Boolean).join(', ');
  return `“${clash.name}” is already a saved customer${id ? ` (${id})` : ''}. Pick them from the suggestions, or add this buyer’s GSTIN or phone to save a different buyer.`;
}
